import { resolveSiteStudyArea } from '../siteStudyArea';
import { selectedAssessmentSedIdentity } from '../generationImpact/selectedAssessmentIntent';
import { sedIntentStampsCurrent } from './shouldBootstrap';
/**
 * R-99c — per-site bootstrap coordinator (server-trigger only).
 *
 * Client-side in-tab dedup, cross-tab mutex, and checkpoint resume were
 * removed after the server path soaked in production.
 */
import type { SiteEntity } from '../../types/site';
import { triggerServerJobAndPoll, type BootstrapResult } from '../../services/sedBootstrap';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { throwIfAborted } from '../abortSignal';
import { loadAppStore } from '../appStore/appStorePort';

/** Default freshness window — skip bootstrap when younger than this. */
export const DEFAULT_SED_BOOTSTRAP_FRESHNESS_MS = 5 * 60 * 1000;

export type SedBootstrapSource =
  | 'fresh'
  | 'skipped-fresh'
  | 'dedup'
  | 'cross-tab-yielded';

export interface SedBootstrapResult {
  source: SedBootstrapSource;
  sedId?: string;
  completeness?: number;
  /** Present when `source === 'skipped-fresh'`. */
  ageMs?: number;
  /**
   * The site's AUTHORITATIVE `sedStatus` at the moment of a freshness skip.
   * Present (and non-'bootstrapping') when `source === 'skipped-fresh'`. The
   * skip path does NOT start a server job, so a caller that optimistically
   * flipped the site to 'bootstrapping' would otherwise leave a PHANTOM
   * "rebuild still working" banner over an already-complete SED. The reconcile
   * restores the site to THIS value rather than guessing — see
   * `retrySEDBootstrap` in the store. A fresh SED is by definition non-failed
   * and non-bootstrapping (see `evaluateSedBootstrapFreshness`), so this is
   * 'complete' or 'partial'.
   */
  liveSedStatus?: SiteEntity['sedStatus'];
  /** Legacy cross-tab timeout field (unused in server-only path). */
  timedOut?: boolean;
}

async function readSiteFromStore(siteId: string): Promise<SiteEntity | undefined> {
  const useStore = await loadAppStore();
  return useStore.getState().siteRag.sites.find(s => s.id === siteId);
}

/**
 * Resolve freshness threshold: explicit override → company setting → default 5 min.
 */
export function resolveFreshnessThresholdMs(
  overrideMs?: number,
  companyThresholdMs?: number | null,
): number {
  if (overrideMs != null && Number.isFinite(overrideMs)) return overrideMs;
  if (companyThresholdMs != null && Number.isFinite(companyThresholdMs)) {
    return companyThresholdMs;
  }
  return DEFAULT_SED_BOOTSTRAP_FRESHNESS_MS;
}

/**
 * Whether the site's SED was bootstrapped within the freshness window.
 * Failed / missing SED always returns not-fresh. A recent build is never
 * fresh when its intent stamp no longer matches the live intent (including a
 * promoted-scenario change) or reviewed evidence sections are dirty: a coarse
 * recent-build timestamp must not hide authority drift (parity with the
 * server router and the admin coordinator).
 */
export function evaluateSedBootstrapFreshness(
  site: SiteEntity,
  thresholdMs: number,
  nowMs = Date.now(),
): { fresh: boolean; ageMs?: number } {
  try {
    if ((resolveSiteStudyArea(site)?.scope.selectionHash ?? null) !== (site.sedBuiltForStudyAreaHash ?? null)) return { fresh: false };
    if ((selectedAssessmentSedIdentity(site.generationAssessmentSelection) ?? null) !== (site.sedBuiltForGenerationAssessmentHash ?? null)) return { fresh: false };
  } catch { return { fresh: false }; }
  const last = site.lastBootstrapAt;
  if (!last) return { fresh: false };
  const then = new Date(last).getTime();
  if (!Number.isFinite(then)) return { fresh: false };
  const ageMs = Math.max(0, nowMs - then);
  if (site.sedStatus === 'failed') return { fresh: false, ageMs };
  if (!site.sedId || !site.sedStatus || site.sedStatus === 'bootstrapping') {
    return { fresh: false, ageMs };
  }
  if ((site.siteEvidenceDirtySections?.length ?? 0) > 0) return { fresh: false, ageMs };
  if (!sedIntentStampsCurrent(site.projectIntent, [site.sedBuiltForIntentHash])) {
    return { fresh: false, ageMs };
  }
  return { fresh: ageMs < thresholdMs, ageMs };
}

async function resolveThresholdForSite(
  site: SiteEntity | undefined,
  overrideMs?: number,
): Promise<number> {
  const useStore = await loadAppStore();
  const { companySedBootstrapFreshnessThresholdMs } = useStore.getState();
  return resolveFreshnessThresholdMs(overrideMs, companySedBootstrapFreshnessThresholdMs);
}

function resultFromBootstrap(
  bootstrapResult: BootstrapResult | null,
  siteAfter?: SiteEntity,
): SedBootstrapResult {
  if (bootstrapResult) {
    return {
      source: 'fresh',
      sedId: bootstrapResult.sedId,
      completeness: bootstrapResult.completeness,
    };
  }
  return {
    source: 'fresh',
    sedId: siteAfter?.sedId,
    completeness: siteAfter?.sedCompleteness ?? 0,
  };
}

/**
 * Coordinated SED bootstrap — freshness gate, in-tab dedup, cross-tab mutex, telemetry.
 */
export async function bootstrapSedCoordinated(
  siteId: string,
  opts?: {
    forceRefresh?: boolean;
    freshnessThresholdMs?: number;
    abortSignal?: AbortSignal;
  },
): Promise<SedBootstrapResult> {
  throwIfAborted(opts?.abortSignal);
  const site = await readSiteFromStore(siteId);
  const thresholdMs = await resolveThresholdForSite(site, opts?.freshnessThresholdMs);
  throwIfAborted(opts?.abortSignal);
  if (!opts?.forceRefresh && site) {
    const { fresh, ageMs } = evaluateSedBootstrapFreshness(site, thresholdMs);
    if (fresh && site.sedId) {
      logAuditEvent({
        type: 'sed-bootstrap-skipped-fresh',
        siteId,
        payload: { ageMs, thresholdMs },
      });
      return {
        source: 'skipped-fresh',
        sedId: site.sedId,
        completeness: site.sedCompleteness ?? 0,
        ageMs,
        // Carry the authoritative live status so the caller can reconcile a
        // phantom optimistic 'bootstrapping' banner back to reality. A fresh SED
        // is never 'failed'/'bootstrapping' (the freshness gate excludes both),
        // so this is the real 'complete' / 'partial' the banner should show.
        liveSedStatus: site.sedStatus,
      };
    }
  }
  logAuditEvent({ type: 'sed-bootstrap-started', siteId });
  const bootstrapResult = await triggerServerJobAndPoll(siteId, {
    forceRefresh: opts?.forceRefresh,
    abortSignal: opts?.abortSignal,
  });
  throwIfAborted(opts?.abortSignal);
  const siteAfter = await readSiteFromStore(siteId);
  throwIfAborted(opts?.abortSignal);
  const result = resultFromBootstrap(bootstrapResult, siteAfter);
  logAuditEvent({
    type: 'sed-bootstrap-completed',
    siteId,
    payload: { sedId: result.sedId, completeness: result.completeness },
  });
  return result;
}
